import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Waitlist.css';

const Waitlist = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');

    // Simulate API call - Replace with actual Mailchimp/ConvertKit/Klaviyo integration
    // Example Mailchimp integration would go here
    setTimeout(() => {
      // For now, just log and show success
      console.log('Waitlist signup:', email);
      setStatus('success');
      setMessage("You're on the list. We'll notify you at launch.");
      setEmail('');
      
      // Reset after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    }, 1500);
  };

  return (
    <section id="waitlist" className="waitlist">
      {/* Background Elements */}
      <div className="waitlist-bg-pattern"></div>
      <div className="waitlist-bg-glow"></div>

      <div className="waitlist-container">
        <motion.div
          className="waitlist-content"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          {/* Badge */}
          <div className="waitlist-badge">
            <span className="badge-icon">◆</span>
            <span className="badge-text">Launching Soon</span>
          </div>

          {/* Heading */}
          <h2 className="waitlist-title">
            Join the <span className="highlight">Inner Circle</span>
          </h2>

          {/* Subtext */}
          <p className="waitlist-description">
            Be the first to access the collection. Get exclusive early access, 
            member-only pricing, and style guides delivered to your inbox.
          </p>

          {/* Benefits */}
          <div className="waitlist-benefits">
            <div className="benefit">
              <span className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <span className="benefit-text">Early Access</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <span className="benefit-text">Launch Discount</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </span>
              <span className="benefit-text">Style Guides</span>
            </div>
          </div>

          {/* Form */}
          <form className="waitlist-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className={`email-input ${status === 'error' ? 'error' : ''}`}
                disabled={status === 'loading' || status === 'success'}
              />
              <button 
                type="submit" 
                className={`submit-btn ${status}`}
                disabled={status === 'loading' || status === 'success'}
              >
                {status === 'loading' ? (
                  <span className="loading-spinner"></span>
                ) : status === 'success' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  'Join Waitlist'
                )}
              </button>
            </div>

            {/* Status Message */}
            <AnimatePresence>
              {message && (
                <motion.p 
                  className={`form-message ${status}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  {status === 'success' && <span className="success-icon">✓</span>}
                  {message}
                </motion.p>
              )}
            </AnimatePresence>
          </form>

          {/* Privacy Note */}
          <p className="privacy-note">
            No spam, ever. Unsubscribe anytime. We respect your inbox.
          </p>

          {/* Social Follow */}
          <div className="waitlist-social">
            <span className="social-label">Follow us for sneak peeks</span>
            <div className="social-links">
              <a 
                href="https://www.instagram.com/slate_and_steel/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link instagram"
                aria-label="Follow on Instagram"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a 
                href="https://pin.it/2ivG62eu2" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link pinterest"
                aria-label="Follow on Pinterest"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
                </svg>
              </a>
            </div>
          </div>
        </motion.div>

        {/* Decorative Elements */}
        <div className="waitlist-decoration left">
          <div className="deco-line"></div>
          <div className="deco-diamond">◆</div>
          <div className="deco-line"></div>
        </div>
        <div className="waitlist-decoration right">
          <div className="deco-line"></div>
          <div className="deco-diamond">◆</div>
          <div className="deco-line"></div>
        </div>
      </div>
    </section>
  );
};

export default Waitlist;





